import React from "react";
import {
  Nav,
  Card,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  ButtonIcon,
  Modal,
  FormInput,
  FormSelect,
  Dropzone,
  Loader,
} from "ui-kit-ck-consultant";
import FadeIn from "react-fade-in";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faEdit, faTrashAlt, faPlus, faCog } from "@fortawesome/free-solid-svg-icons";

import AuthContext from "../../context/AuthContext";
import axiosConfig from "../../utils/axiosConfig";
import downloadFile from "../../utils/downloadFile";

const ALL_COMPANIES = "all";
const ACCEPTED = ["xlsx", "xls", "csv", "pdf", "docx"];

const emptyForm = () => ({ id: null, title: "", companyId: ALL_COMPANIES, file: null });

const formatSize = (bytes) =>
  bytes >= 1048576
    ? `${(bytes / 1048576).toFixed(1)} Mo`
    : `${Math.max(1, Math.round(bytes / 1024))} Ko`;

const errorMessage = (err) =>
  (err.response && err.response.data && err.response.data.message) ||
  "Erreur lors de l'enregistrement";

// Everyone opens and downloads; the super-admin (no company) also uploads, renames, deletes.
export default class Documents extends React.Component {
  static contextType = AuthContext;

  constructor(props) {
    super(props);
    this.state = {
      documents: [],
      companies: [],
      isLoading: true,
      isModal: false,
      form: emptyForm(),
    };
  }

  componentDidMount() {
    this.getDocuments();
    if (this.isManager()) this.getCompanies();
  }

  isManager = () => !this.context.companyId;

  getDocuments = () => {
    this.setState({ isLoading: true });
    axiosConfig("documents/list").then((res) => {
      this.setState({
        documents: res.data.success ? res.data.data : [],
        isLoading: false,
      });
    });
  };

  getCompanies = () => {
    axiosConfig("admin/general/get-all-companies").then((res) => {
      const companies = res.data.success
        ? res.data.data.map((c) => ({ value: Number(c.id), text: c.name }))
        : [];
      this.setState({
        companies: [{ value: ALL_COMPANIES, text: "Toutes les compagnies" }, ...companies],
      });
    });
  };

  openAdd = () => this.setState({ isModal: true, form: emptyForm() });

  openEdit = (doc) =>
    this.setState({
      isModal: true,
      form: {
        id: doc.id,
        title: doc.title,
        companyId: doc.companyId || ALL_COMPANIES,
        file: null,
      },
    });

  closeModal = () => this.setState({ isModal: false });

  handleInputChange = (event) => {
    const { name, value } = event.target;
    this.setState((prev) => ({ form: { ...prev.form, [name]: value } }));
  };

  handleFile = (data) => {
    const file = data ? { name: data.name, base64: data.base64 } : null;
    this.setState((prev) => ({ form: { ...prev.form, file } }));
  };

  handleSave = () => {
    const { form } = this.state;
    axiosConfig({
      url: form.id ? "documents/update" : "documents/create",
      data: {
        id: form.id,
        title: form.title,
        companyId: form.companyId,
        file: form.file,
      },
    })
      .then((res) => {
        if (res.data.success) {
          this.closeModal();
          this.getDocuments();
        } else {
          alert(res.data.message || "Erreur lors de l'enregistrement");
        }
      })
      .catch((err) => alert(errorMessage(err)));
  };

  handleDelete = (doc) => {
    if (!window.confirm(`Supprimer le document « ${doc.title} » ?`)) return;
    axiosConfig({ url: "documents/delete", data: { id: doc.id } })
      .then((res) => {
        if (res.data.success) this.getDocuments();
        else alert(res.data.message || "Erreur lors de la suppression");
      })
      .catch((err) => alert(errorMessage(err)));
  };

  download = (doc) =>
    downloadFile(`documents/${doc.id}/download`).catch(() => alert("Téléchargement impossible"));

  open = (doc) => this.props.history.push(`/documents/${doc.id}`);

  renderModal() {
    const { form, companies } = this.state;
    const canSave = form.title.trim() && (form.id || form.file);

    return (
      <Modal
        title={form.id ? "Modifier le document" : "Ajouter un document"}
        show={this.state.isModal}
        onClose={this.closeModal}
      >
        <FormInput
          className="mb-20"
          type="text"
          name="title"
          title="Titre"
          value={form.title}
          onChange={this.handleInputChange}
        />
        <FormSelect
          className="mb-20"
          name="companyId"
          title="Compagnie"
          value={form.companyId}
          options={companies}
          onChange={this.handleInputChange}
          ignore={true}
        />
        <Dropzone
          className="mb-20"
          dropHandler={this.handleFile}
          typeName={ACCEPTED}
        />
        {form.id && !form.file ? (
          <p className="mb-20">Sans nouveau fichier, le fichier actuel est conservé.</p>
        ) : null}
        <Button text="Enregistrer" onClick={this.handleSave} disabled={!canSave} />
      </Modal>
    );
  }

  render() {
    const manager = this.isManager();
    const { documents } = this.state;

    return (
      <FadeIn>
        <Nav title="Documents" className="mb-20">
          {manager ? (
            <Button text="Ajouter un document" onClick={this.openAdd}>
              <FontAwesomeIcon icon={faPlus} />
            </Button>
          ) : null}
        </Nav>
        <Card>
          <Table empty={documents.length ? "" : "Aucun document disponible"}>
            <Thead>
              <Tr>
                <Th>Titre</Th>
                <Th>Fichier</Th>
                {manager ? <Th>Compagnie</Th> : null}
                <Th>Taille</Th>
                <Th>Modifié par</Th>
                <Th>Le</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {documents.map((doc) => (
                <Tr key={doc.id}>
                  <Td>{doc.title}</Td>
                  <Td>{doc.filename}</Td>
                  {manager ? <Td>{doc.companyName || "Toutes les compagnies"}</Td> : null}
                  <Td className="nowrap">{formatSize(doc.size)}</Td>
                  <Td>{doc.updatedBy || "—"}</Td>
                  <Td className="nowrap">{doc.updatedAt}</Td>
                  <Td className="nowrap">
                    <div className="d-flex">
                      <ButtonIcon
                        small
                        style={{ color: "var(--primary-color)" }}
                        onClick={() => this.open(doc)}
                      >
                        <FontAwesomeIcon icon={faEdit} title="Ouvrir" />
                      </ButtonIcon>
                      <ButtonIcon
                        small
                        style={{ color: "var(--primary-color)" }}
                        onClick={() => this.download(doc)}
                      >
                        <FontAwesomeIcon icon={faDownload} title="Télécharger" />
                      </ButtonIcon>
                      {manager ? (
                        <>
                          <ButtonIcon
                            small
                            style={{ color: "var(--primary-color)" }}
                            onClick={() => this.openEdit(doc)}
                          >
                            <FontAwesomeIcon icon={faCog} title="Titre et compagnie" />
                          </ButtonIcon>
                          <ButtonIcon
                            small
                            style={{ color: "var(--primary-color)" }}
                            onClick={() => this.handleDelete(doc)}
                          >
                            <FontAwesomeIcon icon={faTrashAlt} title="Supprimer" />
                          </ButtonIcon>
                        </>
                      ) : null}
                    </div>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Card>
        {manager ? this.renderModal() : null}
        <Loader show={this.state.isLoading} />
      </FadeIn>
    );
  }
}
